import React, { useState } from 'react';
import { FiLock, FiEye, FiEyeOff, FiShield, FiAlertCircle, FiCheck } from 'react-icons/fi';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({ user, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password minimal 6 karakter';
    }
    if (password === 'Password!') {
      return 'Password baru tidak boleh sama dengan password default';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Konfirmasi password tidak cocok');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/change-password.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError(data.message || 'Gagal mengubah password');
      }
    } catch (err) {
      setError('Terjadi kesalahan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    if (password.length < 6) return { level: 1, text: 'Lemah', color: 'weak' };
    if (password.length < 8) return { level: 2, text: 'Sedang', color: 'medium' };
    if (/[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) {
      return { level: 4, text: 'Sangat Kuat', color: 'very-strong' };
    }
    if (/[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return { level: 3, text: 'Kuat', color: 'strong' };
    }
    return { level: 2, text: 'Sedang', color: 'medium' };
  };

  const strength = passwordStrength(formData.newPassword);

  if (success) {
    return (
      <div className="change-password-modal">
        <div className="change-password-modal__overlay" />
        <div className="change-password-modal__container">
          <div className="change-password-modal__success">
            <div className="change-password-modal__success-icon">
              <FiCheck />
            </div>
            <h2>Password Berhasil Diubah!</h2>
            <p>Anda akan diarahkan ke halaman utama...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="change-password-modal">
      <div className="change-password-modal__overlay" />
      <div className="change-password-modal__container">
        <div className="change-password-modal__header">
          <div className="change-password-modal__icon">
            <FiShield />
          </div>
          <h2>Ubah Password</h2>
          <p>
            Untuk keamanan akun Anda, silakan ubah password default ke password baru yang lebih aman.
          </p>
        </div>

        <div className="change-password-modal__info">
          <FiAlertCircle />
          <span>Anda menggunakan password default. Wajib mengganti password untuk melanjutkan.</span>
        </div>

        {error && (
          <div className="change-password-modal__error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form className="change-password-modal__form" onSubmit={handleSubmit}>
          <div className="change-password-modal__field">
            <label htmlFor="newPassword">Password Baru</label>
            <div className="change-password-modal__input-wrapper">
              <FiLock className="change-password-modal__input-icon" />
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                placeholder="Masukkan password baru"
                value={formData.newPassword}
                onChange={handleChange}
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                className="change-password-modal__toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {formData.newPassword && (
              <div className="change-password-modal__strength">
                <div className="change-password-modal__strength-bar">
                  <div 
                    className={`change-password-modal__strength-fill change-password-modal__strength-fill--${strength.color}`}
                    style={{ width: `${strength.level * 25}%` }}
                  />
                </div>
                <span className={`change-password-modal__strength-text change-password-modal__strength-text--${strength.color}`}>
                  {strength.text}
                </span>
              </div>
            )}
          </div>

          <div className="change-password-modal__field">
            <label htmlFor="confirmPassword">Konfirmasi Password</label>
            <div className="change-password-modal__input-wrapper">
              <FiLock className="change-password-modal__input-icon" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Ulangi password baru"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
              <button
                type="button"
                className="change-password-modal__toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <span className="change-password-modal__match-error">Password tidak cocok</span>
            )}
            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && formData.newPassword.length >= 6 && (
              <span className="change-password-modal__match-success">
                <FiCheck /> Password cocok
              </span>
            )}
          </div>

          <div className="change-password-modal__tips">
            <h4>Tips password yang kuat:</h4>
            <ul>
              <li className={formData.newPassword.length >= 8 ? 'valid' : ''}>Minimal 8 karakter</li>
              <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : ''}>Mengandung huruf kapital</li>
              <li className={/[0-9]/.test(formData.newPassword) ? 'valid' : ''}>Mengandung angka</li>
              <li className={/[^A-Za-z0-9]/.test(formData.newPassword) ? 'valid' : ''}>Mengandung simbol (@, #, !, dll)</li>
            </ul>
          </div>

          <button 
            type="submit" 
            className={`change-password-modal__submit ${loading ? 'loading' : ''}`}
            disabled={loading || !formData.newPassword || !formData.confirmPassword}
          >
            {loading ? (
              <>
                <span className="change-password-modal__spinner"></span>
                Menyimpan...
              </>
            ) : (
              'Simpan Password Baru'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
